import {
  ChakraFlex,
  FireflyButton,
  FAIconName,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';
import { Path } from '~/routes/constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AppointmentStatusCounts, CARD_CONTENT_MIN_HEIGHT, CARD_STYLES, EMPTY_STATE_COPY } from '../constants';
import DashboardCardEmptyState from '../DashboardCardEmptyState';
import DashboardCardErrorState from '../DashboardCardErrorState';
import ProviderDashboardMyDayChart from './ProviderDashboardMyDayChart';

interface ProviderDashboardMyDayCardProps {
  appointments?: AppointmentStatusCounts;
  isLoading?: boolean;
  isError?: boolean;
  onReload: () => void;
  isReloading?: boolean;
}

const MyDaySkeleton = () => (
  <ChakraFlex
    data-testid="my-day-skeleton"
    alignItems="center"
    gap="28px"
    width="100%"
  >
    <ChakraFlex direction="column" gap="4px" width="150px" flexShrink={0}>
      <SkeletonLoader height="24px" width="40px" id="my-day-skeleton-total" startColor="neutral.line" endColor="neutral.background.secondary" />
      <SkeletonLoader height="12px" width="120px" id="my-day-skeleton-label" startColor="neutral.line" endColor="neutral.background.secondary" />
    </ChakraFlex>
    <ChakraFlex direction="column" gap="12px" flex="1" minWidth="0">
      <SkeletonLoader height="14px" width="100%" id="my-day-skeleton-legend" startColor="neutral.line" endColor="neutral.background.secondary" />
      <SkeletonLoader height="8px" width="100%" id="my-day-skeleton-bar" startColor="neutral.line" endColor="neutral.background.secondary" />
    </ChakraFlex>
  </ChakraFlex>
);

const ProviderDashboardMyDayCard = ({
  appointments,
  isLoading = false,
  isError = false,
  onReload,
  isReloading = false,
}: ProviderDashboardMyDayCardProps) => {

  const history = useHistory();
  const { trackEvent } = useAnalytics() ?? {};
  const isEmpty =
    !isLoading && !isError && (!appointments || appointments.total === 0);

  const renderContent = () => {
    if (isLoading) return <MyDaySkeleton />;

    if (isError) {
      return (
        <DashboardCardErrorState
          variant="inline"
          onReload={onReload}
          isReloading={isReloading}
        />
      );
    }

    if (isEmpty) {
      return (
        <DashboardCardEmptyState
          title={EMPTY_STATE_COPY.myDay.title}
          body={EMPTY_STATE_COPY.myDay.body}
        />
      );
    }

    if (appointments) {
      return <ProviderDashboardMyDayChart appointments={appointments} />;
    }

    return null;
  };

  return (
    <ChakraFlex
      data-testid="provider-dashboard-my-day-card"
      direction="column"
      gap="18px"
      {...CARD_STYLES}
      padding="20px 24px"
    >
      <ChakraFlex alignItems="center" justifyContent="space-between" gap="10px">
        <Text variant="h5">My day</Text>
        <FireflyButton
          id="view-calendar-button"
          onClick={() => {
            trackEvent?.(ProviderDashboardEvents.myDayViewCalendarClicked, {
              Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
            });
            history.push(Path.CALENDAR_SCHEDULE);
          }}
          rightIcon={FAIconName.arrowRightRegular}
          variant="tertiary"
          size="small"
        >
          View calendar
        </FireflyButton>
      </ChakraFlex>

      <ChakraFlex
        data-testid="my-day-content-area"
        minHeight={CARD_CONTENT_MIN_HEIGHT.myDay}
        alignItems="center"
        width="100%"
      >
        {renderContent()}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default ProviderDashboardMyDayCard;
